const Review = ({ questions, setCurrentQuestion, questionTimers = [], result = [] }) => {
  const answered = questions.filter(q => q.selectedOption != null).length;
  const bookmarked = questions.filter(q => q.bookMarked).length;
  const unanswered = questions.filter(q => q.selectedOption == null && !q.bookMarked).length;

  const totalTime = questionTimers.reduce((sum, t) => sum + (t || 0), 0);
  const avgTime = answered > 0 ? Math.round(totalTime / answered) : 0;

  const formatTime = (sec) => {
    const s = Math.max(1, sec || 0);
    if (s < 60) return `${s}s`;
    return `${Math.floor(s / 60)}m ${s % 60}s`;
  };

  return (
    <div className="w-full min-h-full overflow-y-auto bg-white">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Section Review</h2>
              <p className="text-xs text-gray-400 mt-0.5">Review your answers before submitting.</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">Progress</p>
              <p className="text-lg font-bold tabular-nums text-gray-800">
                {answered}
                <span className="text-gray-300 text-sm font-normal"> / {questions.length}</span>
              </p>
            </div>
          </div>

          {/* Stats pills */}
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
              <span className="text-xs font-semibold text-blue-600">{answered} Answered</span>
            </div>
            {bookmarked > 0 && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rose-50 border border-rose-200">
                <div className="w-1.5 h-1.5 rounded-full bg-rose-500 flex-shrink-0" />
                <span className="text-xs font-semibold text-rose-600">{bookmarked} Marked for Review</span>
              </div>
            )}
            {unanswered > 0 && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 border border-gray-200">
                <div className="w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />
                <span className="text-xs font-semibold text-gray-500">{unanswered} Unanswered</span>
              </div>
            )}
          </div>

          {/* Time summary */}
          {totalTime > 0 && (
            <div className="flex items-center gap-4 mt-3 px-4 py-2.5 rounded-xl bg-gray-50 border border-gray-100">
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-gray-400 uppercase tracking-wider">Total</span>
                <span className="text-sm font-bold tabular-nums text-gray-700">{formatTime(totalTime)}</span>
              </div>
              <div className="w-px h-4 bg-gray-200" />
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-gray-400 uppercase tracking-wider">Avg / Q</span>
                <span className="text-sm font-bold tabular-nums text-gray-700">{formatTime(avgTime)}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6 pb-28">
        {unanswered > 0 && (
          <div className="mb-5 flex items-center gap-2.5 rounded-xl px-4 py-3 bg-amber-50 border border-amber-200">
            <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <p className="text-sm text-amber-700">
              You have <span className="font-bold text-amber-800">{unanswered}</span> unanswered question{unanswered !== 1 ? 's' : ''}. Click a card to go back.
            </p>
          </div>
        )}

        <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
          {questions.map((ques, idx) => {
            const isBookmarked = ques.bookMarked;
            const isAnswered = ques.selectedOption != null;
            const isCorrect = result[idx] === true;
            const isWrong = result[idx] === false;

            let bgColor, borderColor, numBg, numText, ansText;
            if (isCorrect) {
              bgColor = '#f0fdf4'; borderColor = '#bbf7d0';
              numBg = '#dcfce7'; numText = '#15803d'; ansText = '#15803d';
            } else if (isWrong) {
              bgColor = '#fff1f2'; borderColor = '#fecaca';
              numBg = '#ffe4e6'; numText = '#be123c'; ansText = '#be123c';
            } else if (isBookmarked) {
              bgColor = '#fff1f2'; borderColor = '#fecdd3';
              numBg = '#ffe4e6'; numText = '#e11d48'; ansText = '#e11d48';
            } else if (isAnswered) {
              bgColor = '#eff6ff'; borderColor = '#bfdbfe';
              numBg = '#dbeafe'; numText = '#1d4ed8'; ansText = '#1d4ed8';
            } else {
              bgColor = '#f9fafb'; borderColor = '#e5e7eb';
              numBg = '#f3f4f6'; numText = '#9ca3af'; ansText = '#9ca3af';
            }

            return (
              <button
                key={idx}
                onClick={() => setCurrentQuestion(questions[idx])}
                className="flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border transition-all duration-150 hover:shadow-md hover:scale-105"
                style={{ background: bgColor, borderColor }}
              >
                {/* Number bubble */}
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs flex-shrink-0"
                  style={{ background: numBg, color: numText }}
                >
                  {idx + 1}
                </div>

                {/* Answer */}
                <span className="text-sm font-bold" style={{ color: ansText }}>
                  {isAnswered ? ques.selectedOption.toUpperCase() : '—'}
                </span>

                {/* Status badges */}
                {isCorrect && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-600 leading-none">
                    ✓
                  </span>
                )}
                {isBookmarked && !isCorrect && !isWrong && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-500 leading-none">
                    ★
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Review;
